/**
 * Zustand store — the ONE shared source of state for both the R3F canvas and the
 * DOM HUD (ARCHITECTURE.md §2.2). The client holds NO authoritative game state:
 * everything under `server` is a verbatim projection of the latest
 * Server→Client messages (CLAUDE.md rule 1). Local-only UI/identity lives under
 * the top level. We never re-derive game logic here.
 */

import { create } from 'zustand';
import type {
  Card,
  GamePhase,
  Hand,
  HandValue,
  ParticipantStatus,
  Payout,
  RankedParticipant,
  Role,
  ServerMessage,
  RosterEntry,
  LeaderboardEntry,
  PublicHandView,
  TieGroup,
  DiceRoundView,
} from '@blackjack/shared';
import { DEFAULT_DECKS, DEFAULT_TURN_TIMER_SECS, HOUSE_ID } from '@blackjack/shared';

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

/** This client's own private hand (only ever its own cards + others' up-cards). */
export interface OwnHand {
  readonly hand: Hand;
  readonly total: number;
  readonly isSoft: boolean;
  readonly status: ParticipantStatus;
}

/** The latest resolution snapshot (all hands public at this point). */
export interface ResolutionSnapshot {
  readonly ranking: readonly RankedParticipant[];
  readonly hands: readonly { readonly id: string; readonly hand: Hand; readonly value: HandValue }[];
  readonly winnerId: string | null;
  readonly worstLoserId: string | null;
  readonly ties: readonly TieGroup[];
}

/** The latest dice tiebreak the client must animate deterministically from `seed`. */
export interface DiceSnapshot {
  readonly slot: 'winner' | 'worstLoser';
  readonly seed: number;
  readonly contestants: readonly string[];
  readonly rounds: readonly DiceRoundView[];
  readonly winnerId: string;
  /** Monotonic key so the scene can re-trigger the animation on each new roll. */
  readonly key: number;
}

/** Final round outcome + balance deltas. */
export interface ResultSnapshot {
  readonly winnerId: string;
  readonly worstLoserId: string;
  readonly hostNextId: string | null;
  readonly payouts: readonly Payout[];
}

/** A short-lived gold slot-machine announcement to pop in the HUD. */
export interface Announcement {
  readonly id: number;
  readonly text: string;
  readonly tone: 'win' | 'lose' | 'neutral' | 'tie' | 'bust';
}

/** Server-projected state — mirror of the authoritative server, never mutated locally. */
export interface ServerState {
  readonly phase: GamePhase;
  readonly deadlineTs: number | undefined;
  readonly config: { readonly decks: number; readonly turnTimerSecs: number; readonly dealerRule: 'S17' | 'H17' };
  readonly roster: readonly RosterEntry[];
  readonly leaderboard: readonly LeaderboardEntry[];
  readonly ownHand: OwnHand | undefined;
  readonly publicUpcards: readonly PublicHandView[];
  readonly resolution: ResolutionSnapshot | undefined;
  /** Dice tiebreaks for this round, one per tied category (winner / worstLoser). */
  readonly diceRolls: readonly DiceSnapshot[];
  readonly result: ResultSnapshot | undefined;
}

const initialServerState: ServerState = {
  phase: 'LOBBY',
  deadlineTs: undefined,
  config: { decks: DEFAULT_DECKS, turnTimerSecs: DEFAULT_TURN_TIMER_SECS, dealerRule: 'S17' },
  roster: [],
  leaderboard: [],
  ownHand: undefined,
  publicUpcards: [],
  resolution: undefined,
  diceRolls: [],
  result: undefined,
};

/** The paced sub-beats the client plays during a TIEBREAK (server holds one window). */
export type TiebreakBeat = 'idle' | 'intro' | 'countdown' | 'throw' | 'results';

/** Persisted local identity (mirrored to localStorage). */
export interface Identity {
  readonly clientId: string;
  readonly name: string;
  readonly role: Role;
}

export interface GameStore {
  // --- connection / identity (local only) ---
  readonly connection: ConnectionStatus;
  readonly serverAssignedId: string | undefined;
  readonly protocolMismatch: boolean;
  readonly joined: boolean;
  readonly identity: Identity | undefined;
  readonly lastError: { readonly code: string; readonly message: string } | undefined;
  readonly audioUnlocked: boolean;

  // --- server-projected state ---
  readonly server: ServerState;

  // --- derived HUD ephemera ---
  readonly announcements: readonly Announcement[];
  readonly diceAnimationKey: number;
  /** Current sub-beat of the paced tiebreak presentation (driven client-side). */
  readonly tiebreakBeat: TiebreakBeat;

  // --- actions: local mutators ---
  setConnection(status: ConnectionStatus): void;
  setIdentity(identity: Identity): void;
  setJoined(joined: boolean): void;
  setAudioUnlocked(): void;
  pushAnnouncement(text: string, tone: Announcement['tone']): void;
  dismissAnnouncement(id: number): void;
  setTiebreakBeat(beat: TiebreakBeat): void;

  // --- the single ingestion point for every Server→Client message ---
  ingest(msg: ServerMessage): void;
}

let announcementSeq = 0;
let diceSeq = 0;

export const useGameStore = create<GameStore>((set, get) => ({
  connection: 'idle',
  serverAssignedId: undefined,
  protocolMismatch: false,
  joined: false,
  identity: undefined,
  lastError: undefined,
  audioUnlocked: false,

  server: initialServerState,

  announcements: [],
  diceAnimationKey: 0,
  tiebreakBeat: 'idle',

  setConnection: (status) => set({ connection: status }),
  setIdentity: (identity) => set({ identity }),
  setJoined: (joined) => set({ joined }),
  setAudioUnlocked: () => set({ audioUnlocked: true }),
  setTiebreakBeat: (beat) => set({ tiebreakBeat: beat }),

  pushAnnouncement: (text, tone) =>
    // REPLACE the queue (don't append): the HUD only ever shows the latest, and a
    // growing queue made an earlier message RESURFACE once the later one dismissed
    // (the "BUST! reappears" bug). One announcement at a time.
    set(() => ({
      announcements: [{ id: ++announcementSeq, text, tone }],
    })),

  dismissAnnouncement: (id) =>
    set((s) => ({ announcements: s.announcements.filter((a) => a.id !== id) })),

  ingest: (msg) => {
    switch (msg.type) {
      case 'hello': {
        // Verify protocol compatibility (the client must consume PROTOCOL_VERSION).
        set({ serverAssignedId: msg.yourId });
        return;
      }
      case 'roster':
        set((s) => ({ server: { ...s.server, roster: msg.roster } }));
        return;
      case 'leaderboard':
        set((s) => ({ server: { ...s.server, leaderboard: msg.leaderboard } }));
        return;
      case 'phase': {
        const prevPhase = get().server.phase;
        set((s) => ({
          server: {
            ...s.server,
            phase: msg.phase,
            deadlineTs: msg.deadlineTs,
            // Keep last-known config if the message omits it.
            config: msg.config ?? s.server.config,
            // Clear round-scoped snapshots when we cycle back to LOBBY/BETTING.
            ...(msg.phase === 'LOBBY' || msg.phase === 'BETTING'
              ? {
                  ownHand: undefined,
                  publicUpcards: [],
                  resolution: undefined,
                  diceRolls: [],
                  result: undefined,
                }
              : {}),
          },
        }));
        // Tiebreak sub-beats are driven by TiebreakDirector; reset to idle whenever
        // we leave TIEBREAK.
        if (msg.phase !== 'TIEBREAK' && get().tiebreakBeat !== 'idle') {
          set({ tiebreakBeat: 'idle' });
        }
        if (prevPhase !== msg.phase) maybeAnnouncePhase(get(), msg.phase);
        return;
      }
      case 'deal':
        set((s) => ({
          server: {
            ...s.server,
            ownHand: {
              hand: msg.yourHand,
              total: handTotal(msg.yourHand),
              isSoft: false,
              status: 'playing',
            },
            publicUpcards: msg.publicUpcards,
          },
        }));
        return;
      case 'youUpdate': {
        const prevStatus = get().server.ownHand?.status;
        set((s) => ({
          server: {
            ...s.server,
            ownHand: {
              hand: msg.hand,
              total: msg.total,
              isSoft: msg.isSoft,
              status: msg.status,
            },
          },
        }));
        // Red "BUST!" pop the moment this client's own hand busts (once).
        if (msg.status === 'bust' && prevStatus !== 'bust') {
          get().pushAnnouncement('BUST!', 'bust');
        }
        return;
      }
      case 'resolution':
        set((s) => ({
          server: {
            ...s.server,
            resolution: {
              ranking: msg.ranking,
              hands: msg.hands,
              winnerId: msg.winnerId,
              worstLoserId: msg.worstLoserId,
              ties: msg.ties,
            },
          },
        }));
        return;
      case 'diceRoll':
        set((s) => ({
          diceAnimationKey: ++diceSeq,
          server: {
            ...s.server,
            // One roll per category; replace any prior roll for the same slot.
            diceRolls: [
              ...s.server.diceRolls.filter((d) => d.slot !== msg.slot),
              {
                slot: msg.slot,
                seed: msg.seed,
                contestants: msg.contestants,
                rounds: msg.rounds,
                winnerId: msg.winnerId,
                key: diceSeq,
              },
            ],
          },
        }));
        return;
      case 'result':
        set((s) => ({
          server: {
            ...s.server,
            result: {
              winnerId: msg.winnerId,
              worstLoserId: msg.worstLoserId,
              hostNextId: msg.hostNextId,
              payouts: msg.payouts,
            },
          },
        }));
        announceHost(get(), msg.hostNextId);
        return;
      case 'error':
        set({ lastError: { code: msg.code, message: msg.message } });
        return;
      case 'pong':
        // Heartbeat ack — liveness only; nothing to store.
        return;
    }
  },
}));

/**
 * Display-only total for the initial deal (the `youUpdate` message carries the
 * authoritative total/isSoft afterward). This mirrors the engine's value rule but
 * is used ONLY for the brief moment between `deal` and the first `youUpdate`;
 * the server remains authoritative.
 */
function handTotal(hand: readonly Card[]): number {
  let total = 0;
  let aces = 0;
  for (const c of hand) {
    if (c.rank === 'A') {
      aces += 1;
      total += 11;
    } else if (c.rank === 'K' || c.rank === 'Q' || c.rank === 'J') {
      total += 10;
    } else {
      total += Number(c.rank);
    }
  }
  while (total > 21 && aces > 0) {
    total -= 10;
    aces -= 1;
  }
  return total;
}

/** This client's stable id (server-assigned, falling back to local identity). */
function selfIdOf(s: GameStore): string | undefined {
  return s.serverAssignedId ?? s.identity?.clientId;
}

/** Display name for a participant id (house resolves to "the House"). */
function nameOf(s: GameStore, id: string | null | undefined): string {
  if (id == null) return 'Someone';
  if (id === HOUSE_ID) return 'the House';
  return s.server.roster.find((r) => r.id === id)?.name ?? 'Someone';
}

/**
 * Phase-driven announcements. The gold slot-machine pops at the winner reveal
 * (RESULT). The winner is resolved by the time RESULT is entered (the
 * post-tiebreak `resolution` message is ingested just before the phase
 * message), so we read it from the freshly-set snapshot. The *host* callout is
 * driven separately by the `result` message (see {@link announceHost}). The
 * TIEBREAK has its own paced panels (see Tiebreak.tsx), so it does NOT use the
 * transient announcement.
 */
function maybeAnnouncePhase(store: GameStore, phase: GamePhase): void {
  if (phase === 'RESULT') {
    const winnerId = store.server.resolution?.winnerId ?? store.server.result?.winnerId ?? null;
    if (winnerId == null) return;
    const me = selfIdOf(store);
    // The croupier owns the house hand, so a house win is the croupier's win.
    const iWon = winnerId === me || (winnerId === HOUSE_ID && store.identity?.role === 'croupier');
    if (iWon) store.pushAnnouncement('You win!', 'win');
    else store.pushAnnouncement(`${nameOf(store, winnerId)} wins`, 'neutral');
  }
}

/** "You host tomorrow!" / "{name} hosts tomorrow" once the host is decided. */
function announceHost(store: GameStore, hostNextId: string | null): void {
  if (hostNextId == null) return;
  const me = selfIdOf(store);
  if (hostNextId === me) store.pushAnnouncement('You host tomorrow!', 'win');
  else store.pushAnnouncement(`${nameOf(store, hostNextId)} hosts tomorrow`, 'neutral');
}
